import React, { useState } from 'react';
import { Field, FieldArray, change } from 'redux-form';
import { t } from 'i18next';
import AddressComplete from '../../ReactComponent/AddressComplete';
import RenderFieldForm from '../../ReactComponent/RenderFieldForm';
import RenderField from '../../ReactComponent/RenderField';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import CheckMarkSolidOrange from '../../../assets/icons/CheckMarkSolidOrange.svg';

import {
  isRequired, isPhone, isEmail, maxChar50,
  maxChar40,
  maxChar5,
} from '../../../utils/validator';
import { phoneNumber, digitOnly } from '../../../utils/mask';
import {
  PageTitle,
  ContentBoxLightGrey,
  CategoryTitle,
  CategoryText,
  CardTitle,
  ItemContainer,
  TextProperty,
  FieldsSpacer,
  TimeContainer,
  TimeInputsContainer,
  TimeDivider,
  Grouping,
  UserFormDetails,
} from '../css';
import { H3, P } from '../../../theme/typography';
import {
  BillingAccountType, UserProfile, AddressTypes, AMPM, IsYesOrNo,
} from '../../../utils/enums';
import { NavToPageAndShowAddNewLineButton } from '../../../utils/func';
import { isNullOrEmpty } from '../../../utils/arrays';
import getFieldValue from '../../../utils/sitecore-utils';
import { billingAccountTextFormatter, billingAccountToNumber } from '../../../utils/form';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';
import ToggleButton from '../../../lib/ui/buttons/ToggleButton';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import PasswordInput from '../../../lib/ui/form_elements/PasswordInput';
import PasswordGroup from '../../ReactComponent/PasswordGroup';
import TermsAndConditionsPreferences from './TermsAndConditionsPreferences';

const companyCheckboxRenderer = ({ item }) => (
  <>
    <Column $tablet={6} $mobile={12}>
      <H3>{item.accountNumber || item.permissionName}</H3>
    </Column>
    {item.accountType === 'PA'
      && (
      <Column $tablet={6} $mobile={12}>
        <H3>{t('Protected Billing Account')}</H3>
      </Column>
      )}
  </>
);

const renderUserCompanyList = ({
  // eslint-disable-next-line no-unused-vars
  fields, dispatch, formName, formData, valid, companiesList, currentCompaniesList, getCompanyInfo, companyDetails, showAdd,
}) => {
  const [state, setState] = useState({
    added: showAdd,
  });

  const setAddedTrue = () => setState({
    ...state,
    added: true,
  });

  NavToPageAndShowAddNewLineButton(currentCompaniesList, state.added, setAddedTrue);
  return (
    <>
      {fields.map((item, index) => {
        const fieldValue = fields.get(index);
        const {
          completed,
          companyId,
          accounts,
          permissions,
        } = fieldValue;

        const onlyUsefulPermissions = permissions && permissions.length > 0
          ? permissions.filter((permission) =>
            permission.permissionName !== 'CreateShipment'
          && permission.permissionName !== 'InterfaceSetting'
          && permission.permissionName !== 'PrintLabels'
          && permission.permissionName !== 'QuickEstimate')
          : permissions;

        if (completed) {
          return (
            <ContentBoxLightGrey key={index}>
              <CardTitle>
                {t('Company')}
                {` ${index + 1}`}
              </CardTitle>
              <Row>
                <Column>
                  <TextProperty>
                    <P className="title">{t('SelectedCompany')}</P>
                    <P className="text">{companiesList.find((c) => c.companyId === companyId)?.companyName || companyId}</P>
                  </TextProperty>
                </Column>
              </Row>
              <Row>
                <Column>
                  <TextProperty>
                    <P className="title">{t('BillingAccountNumber')}</P>
                    {accounts.map((a) =>
                      a.accountNumber
                      && (
                      <P key={a.accountNumber} className="text">
                        <img src={CheckMarkSolidOrange} alt="added item" />
                        {a.accountNumber}
                        {' '}
                        {`- ${a.accountType === 'PA'
                          ? t(BillingAccountType.Protected)
                          : t(BillingAccountType.Billing)}`}
                      </P>
                      ),
                    )}
                  </TextProperty>
                </Column>
              </Row>
              <Row>
                <Column>
                  <TextProperty>
                    <P className="title">{t('AssignedPermissions')}</P>
                    {onlyUsefulPermissions && onlyUsefulPermissions.length > 0 ? onlyUsefulPermissions.map((p) =>
                      p.permissionId
                      && (
                      <P key={p.permissionId} className="text">
                        <img src={CheckMarkSolidOrange} alt="added item" />
                        {p.permissionName && p.permissionName === 'ShipmentTracking'
                          ? 'Invoices'
                          : p.permissionName && p.permissionName === 'ManageAddressBooks'
                            ? 'Manage Contacts'
                            : p.permissionName && p.permissionName === 'ManageAccounts'
                              ? 'Manage Companies'
                              : p.permissionName}
                      </P>
                      ),
                    ) : []}
                  </TextProperty>
                </Column>
              </Row>
            </ContentBoxLightGrey>
          );
        }

        return (
          <ContentBoxLightGrey className="edit-item-box" key={index}>
            <CardTitle>
              {t('Company')}
              {` ${index + 1}`}
            </CardTitle>
            <Row>
              <Column $tablet={5} $mobile={12}>
                <Field
                  name={`${item}.companyId`}
                  type="dropdown"
                  label={t('SelectedCompany')}
                  data={companiesList}
                  textField="companyName"
                  valueField="companyId"
                  required
                  onChange={(e) => getCompanyInfo(e, index)}
                  component={RenderField}
                  validate={[isRequired]}
                />
              </Column>
            </Row>
            {formData.companies && formData.companies[index] && formData.companies[index].companyId
              && (
              <>
                <Row>
                  <Column>
                    <TextProperty>
                      <P className="text">{t('BillingAccountNumber')}</P>
                      <P className="title">{t('SelectBillingAccount')}</P>
                    </TextProperty>
                  </Column>
                </Row>
                <Row>
                  <Column $tablet={6} $mobile={12}>
                    <Field
                      name={`${item}.accounts`}
                      type="checkboxes"
                      label={t('SelectedAccountNumbers')}
                      data={companyDetails[index] && companyDetails[index].accounts}
                      renderer={companyCheckboxRenderer}
                      className="w-50"
                      required
                      multiple
                      component={RenderField}
                      validate={[isRequired]}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <TextProperty>
                      <P className="text">{t('UserPermissions')}</P>
                    </TextProperty>
                  </Column>
                </Row>
                <Row>
                  <Column $tablet={6} $mobile={12}>
                    <Field
                      name={`${item}.permissions`}
                      type="checkboxes"
                      label={t('SelectedPermissionNumbers')}
                      data={companyDetails[index] && companyDetails[index].permissions}
                      renderer={companyCheckboxRenderer}
                      className="w-50"
                      required
                      multiple
                      component={RenderFieldForm}
                      validate={[isRequired]}
                    />
                  </Column>
                </Row>
              </>
              )}
          </ContentBoxLightGrey>
        );
      })}
    </>
  );
};

const UserForm = ({
  fields,
  // eslint-disable-next-line no-unused-vars
  division,
  formData = {},
  formName,
  dispatch,
  companiesList,
  getCompanyInfo,
  companyDetails,
  valid,
  showAdd,
  selectedTab,
  contacts,
  billingAccounts,
  changeBillTo,
}) => {
  const {
    companies, rowVersion, email, isPickupScheduled, oldPassword,
  } = formData;
  const {
    DefaultOverrideAddressTitle,
    ShipperPostalZipcode,
    ShipperContactName,
    ConsigneePostalZipcode,
    USQuoteDefaults,
  } = fields;
  const update = !!rowVersion;
  const renderProfileInformation = () => (
    <div>
      <PageTitle className="justified">
        {fields.pageTitle && fields.pageTitle.value}
      </PageTitle>
      <Row className="justify-content-md-center">
        <Column $tablet={12} $mobile={12}>
          <TextProperty>
            {fields.emailAddressInfo && fields.emailAddressInfo.value}
          </TextProperty>
        </Column>
      </Row>
      <Row>
        <Column $tablet={6} $mobile={12}>
          {update
            ? (
              <TextProperty>
                <P className="title">{t('Email')}</P>
                <P className="text">{email}</P>
              </TextProperty>
            )
            : (
              <Field
                name="email"
                type="text"
                required
                component={TextInput}
                label={t('Email')}
                validate={[isRequired, isEmail]}
              />
            )}
        </Column>
      </Row>

      {update && (
        <>
          <CategoryTitle style={{ marginTop: '32px' }}>
            {fields.passwordTitle && fields.passwordTitle.value}
          </CategoryTitle>
          <TextProperty>
            {fields.passwordSummary && fields.passwordSummary.value}
          </TextProperty>
          <Row>
            <Column $tablet={6} $mobile={12}>
              <Field
                name="oldPassword"
                type="password"
                required
                component={PasswordInput}
                label={t('OldPassword')}
                validate={[]}
              />
            </Column>
          </Row>
          {oldPassword && <PasswordGroup passwordImage={fields.passwordImage} />}
        </>
      )}

      <CategoryTitle style={{ marginTop: '32px' }}>
        {fields.profileDetailsTitle && fields.profileDetailsTitle.value}
      </CategoryTitle>
      <UserFormDetails>
        <Row>
          <Column $tablet={12} $mobile={12}>
            <Field
              name="companyName"
              type="text"
              required
              component={TextInput}
              label={t('CompanyName')}
              validate={[isRequired, maxChar40]}
              maxLength={40}
            />
          </Column>
        </Row>
        <Row>
          <Column $tablet={6} $mobile={12}>
            <Field
              name="name"
              type="text"
              required
              component={TextInput}
              label={t('FullName')}
              validate={[isRequired, maxChar40]}
              maxLength={40}
            />
          </Column>
          <Column $tablet={6} $mobile={12}>
            <Field
              name="title"
              type="text"
              component={TextInput}
              label={t('Title')}
              validate={[maxChar40]}
              maxLength={40}
            />
          </Column>
        </Row>
        <Row>
          <Column $tablet={12} $mobile={12}>
            <AddressComplete
              fieldName="address"
              formName={formName}
              required
            />
          </Column>
        </Row>
        <Row>
          <Column $tablet={6} $mobile={12}>
            <Field
              name="phoneNumber"
              type="text"
              component={TextInput}
              label={t('PhoneNumber')}
              validate={[isPhone, maxChar50, isRequired]}
              normalize={phoneNumber}
              required
            />
          </Column>
          <Column $tablet={6} $mobile={12}>
            <Field
              name="phoneNumberExt"
              type="text"
              component={TextInput}
              label={t('Extension')}
              validate={[maxChar5]}
              normalize={digitOnly}
              maxLength={5}
            />
          </Column>
        </Row>
        <Row>
          <Column $tablet={12} $mobile={12}>
            <Field
              name="description"
              type="text"
              required
              component={TextInput}
              label={t('Description')}
              validate={[isRequired, maxChar40]}
              maxLength={40}
            />
          </Column>
        </Row>
      </UserFormDetails>
    </div>
  );

  const renderAccountPrivelages = () => (
    <div>
      <Row>
        <Column>
          <CategoryTitle>
            {fields.Tab2PageTitle && fields.Tab2PageTitle.value}
          </CategoryTitle>
        </Column>
      </Row>
      <Row className="justify-content-md-center">
        <Column $tablet={12} $mobile={12}>
          <TextProperty>
            {fields.accountPrivileges && fields.accountPrivileges.value}
          </TextProperty>
        </Column>
      </Row>
      <Row>
        <Column>
          <ItemContainer>
            <FieldArray
              name="companies"
              dispatch={dispatch}
              component={renderUserCompanyList}
              valid={valid}
              formName={formName}
              companiesList={companiesList}
              currentCompaniesList={companies}
              getCompanyInfo={getCompanyInfo}
              companyDetails={companyDetails}
              formData={formData}
              showAdd={showAdd}
            />
          </ItemContainer>
        </Column>
      </Row>
    </div>
  );

  const getContactsByType = (type) => {
    if (isNullOrEmpty(contacts)) {
      return [];
    }

    return contacts.filter((contact) => [type, AddressTypes.SHARED, AddressTypes.OTHER]
      .includes(contact.contactType
        .toLowerCase()))
      .map((item) => ({ contactId: item.contactId, text: item.nickname || item.contactName }));
  };

  const renderUserPreferences = () => (
    <FieldsSpacer>
      <Row>
        <Column>
          <CategoryTitle>
            {t('Billing')}
          </CategoryTitle>
          <Row>
            <Column>
              <CategoryText className="marginBottom">
                {t('BillingLabel')}
              </CategoryText>
              <Field
                name="billToAccountNumber"
                type="dropdown"
                label={t('BillToAccountNumber')}
                data={billingAccounts}
                textField={billingAccountTextFormatter}
                valueField="accountNumber"
                component={RenderFieldForm}
                normalize={billingAccountToNumber}
                onChange={(billTo) => changeBillTo(billTo)}
                clearable
                onReset={() => dispatch(change(formName, 'billToAccountNumber', null))}
              />
            </Column>

          </Row>
        </Column>
      </Row>
      <Row>
        <Column>
          <CategoryTitle className="noMarginBottom">
            {t('PickupDetails')}
          </CategoryTitle>
          <Row>
            <Column $desktop={6} $tablet={12}>
              <CategoryTitle className="marginTop">
                {t('RegularScheduledPickupTitle')}
              </CategoryTitle>
            </Column>
            <Column $desktop={6} $tablet={12}>
              <Field
                name="isPickupScheduled"
                component={RenderFieldForm}
                type="radio"
                value={isPickupScheduled}
                data={[
                  { name: IsYesOrNo.Yes, value: true },
                  { name: IsYesOrNo.No, value: false },
                ]}
              />
            </Column>
          </Row>
        </Column>
      </Row>
      <Row>
        <Column $desktop={6} $tablet={12}>
          <CategoryTitle>
            {t('ReadyTime')}
            :
          </CategoryTitle>
          <TimeContainer>
            <TimeInputsContainer>
              <Field
                name="readyTimeHour"
                label={t('Hour')}
                component={Dropdown}
                options={[
                  { value: '' },
                  ...Array(12).keys()].map((item) => (typeof item === 'object' ? item : { value: item + 1 }))}
              />
              <TimeDivider>:</TimeDivider>
              <Field
                name="readyTimeMinutes"
                label={t('Minutes')}
                component={Dropdown}
                options={['', '00', '15', '30', '45'].map((item) => ({ value: item }))}
              />
            </TimeInputsContainer>
            <Field
              name="readyAMPM"
              component={ToggleButton}
              label1={AMPM.AM}
              label2={AMPM.PM}
            />
          </TimeContainer>
        </Column>
        <Column $desktop={6} $tablet={12}>
          <CategoryTitle>
            {t('ClosingTime')}
            :
          </CategoryTitle>
          <TimeContainer>
            <TimeInputsContainer>
              <Field
                name="closingTimeHour"
                label={t('Hours')}
                component={Dropdown}
                options={[
                  { value: '' },
                  ...Array(12).keys()].map((item) => (typeof item === 'object' ? item : { value: item + 1 }))}
              />
              <TimeDivider>:</TimeDivider>
              <Field
                name="closingTimeMinutes"
                label={t('Minutes')}
                component={Dropdown}
                options={['', '00', '15', '30', '45'].map((item) => ({ value: item }))}
              />
            </TimeInputsContainer>
            <Field
              name="closingAMPM"
              component={ToggleButton}
              label1={AMPM.AM}
              label2={AMPM.PM}
            />
          </TimeContainer>
        </Column>
      </Row>
      <Row className="justify-content-md-center">
        <Column $desktop={12} $mobile={12}>
          <CategoryTitle>{t('ShipmentDefaults')}</CategoryTitle>
          <Grouping>
            <Row>
              <Column className="squeeze" $desktop={6} $mobile={12}>
                <Field
                  name="fromContact"
                  type="dropdown"
                  component={RenderFieldForm}
                  data={getContactsByType(AddressTypes.SHIPPER)}
                  textField="text"
                  valueField="contactId"
                  label={t('FromAddress')}
                  placeholder={t('Select')}
                  clearable
                  onReset={() => dispatch(change(formName, 'fromContact', null))}
                />
              </Column>
              <Column $desktop={6}>
                <Field
                  name="toContact"
                  type="dropdown"
                  component={RenderFieldForm}
                  data={getContactsByType(AddressTypes.CONSIGNEE)}
                  textField="text"
                  valueField="contactId"
                  label={t('ToAddress')}
                  clearable
                  onReset={() => dispatch(change(formName, 'toContact', null))}
                />
              </Column>
            </Row>
          </Grouping>
        </Column>
      </Row>
      <Row className="justify-content-md-center">
        <Column $desktop={12}>
          <CategoryTitle>
            {t('AddressOverrides')}
            {' '}
            (
            {t('Blind')}
            )
          </CategoryTitle>
          <Grouping>
            <Row>
              <Column className="squeeze" $desktop={6} $tablet={12} $mobile={12}>
                <Field
                  name="overrideContact"
                  type="dropdown"
                  component={RenderFieldForm}
                  data={getContactsByType(AddressTypes.SHIPPER)}
                  textField="text"
                  valueField="contactId"
                  label={getFieldValue(DefaultOverrideAddressTitle)}
                  clearable
                  onReset={() => dispatch(change(formName, 'overrideContact', null))}
                />
              </Column>
              <Column $desktop={6} $tablet={12}>
                <Field
                  name="overrideConsigneeAddress"
                  type="dropdown"
                  component={RenderFieldForm}
                  data={getContactsByType(AddressTypes.CONSIGNEE)}
                  textField="text"
                  valueField="contactId"
                  label={t('DefaultOverrideAddressConsignee')}
                  clearable
                  onReset={() => dispatch(change(formName, 'overrideConsigneeAddress', null))}
                />
              </Column>
            </Row>
            <Row style={{ marginTop: '16px' }}>
              <Column $desktop={12}>
                <p style={{ fontSize: '14px', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                  {t('OverrideAddress1')}
                </p>
                <p style={{ fontSize: '14px', lineHeight: '1.6', margin: '0 0 8px 0' }}>
                  {t('OverrideAddress2')}
                </p>
                <p style={{
                  fontSize: '14px', lineHeight: '1.6', margin: '0', fontStyle: 'italic', fontWeight: 'bold',
                }}
                >
                  {t('OverrideAddress3')}
                </p>
              </Column>
            </Row>
          </Grouping>
        </Column>
      </Row>
      <Row>
        <Column>
          <CategoryTitle>
            {getFieldValue(USQuoteDefaults)}
          </CategoryTitle>
          <UserFormDetails>
            <Row>
              <Column>
                <Field
                  name="shipperName"
                  type="text"
                  component={TextInput}
                  label={getFieldValue(ShipperContactName)}
                />
              </Column>
            </Row>
            <Row>
              <Column $desktop={6} $tablet={12} $mobile={12}>
                <Field
                  name="shipperPostalCode"
                  type="text"
                  component={TextInput}
                  label={getFieldValue(ShipperPostalZipcode)}
                  maxLength={7}
                />
              </Column>
              <Column $desktop={6} $tablet={12}>
                <Field
                  name="consigneePostalCode"
                  type="text"
                  component={TextInput}
                  label={getFieldValue(ConsigneePostalZipcode)}
                  maxLength={7}
                />
              </Column>
            </Row>
          </UserFormDetails>
        </Column>
      </Row>
      <Row>
        <Column>
          <CategoryTitle>
            {t('CustomsBroker')}
          </CategoryTitle>
          <UserFormDetails>
            <Row>
              <Column>
                <Field
                  name="brokerName"
                  type="text"
                  component={TextInput}
                  label={t('BrokerNameUserPref')}
                />
              </Column>
            </Row>
            <Row>
              <Column $desktop={6} $tablet={12}>
                <Field
                  name="brokerEmail"
                  type="text"
                  component={TextInput}
                  valueField="brokerEmail"
                  label={t('BrokerEmail')}
                  validate={[isEmail]}
                />
              </Column>
              <Column $desktop={6} $tablet={12}>
                <Field
                  name="brokerTelephone"
                  type="text"
                  component={TextInput}
                  valueField="brokerTelephone"
                  label={t('BrokerTelephone')}
                  validate={[isPhone, maxChar50]}
                  normalize={phoneNumber}
                />
              </Column>
            </Row>
          </UserFormDetails>
        </Column>
      </Row>
      <Row>
        <Column>
          <TermsAndConditionsPreferences />
        </Column>
      </Row>
    </FieldsSpacer>
  );

  let renderedTab = null;
  switch (selectedTab) {
    case UserProfile.PROFILE_INFORMATION:
      renderedTab = renderProfileInformation();
      break;
    case UserProfile.ACCOUNT_PRIVILEAGES:
      renderedTab = renderAccountPrivelages();
      break;
    case UserProfile.USER_PREFERENCES:
      renderedTab = renderUserPreferences();
      break;
    default:
      renderedTab = null;
  }

  return renderedTab;
};

export default UserForm;
